import { ReactNode, useMemo, useState } from "react";
import { DebouncedSearchInput } from "../../UI/DebouncedSearchInput.tsx";
import { SaleRecord } from "./SaleRecord.ts";
import {
  CustomerOrders,
  customerKeyOf,
  matchesCustomerSearch,
  newestOrdersFirst,
  ordersByCustomer,
} from "./salesCustomers.ts";
import { SalesPeriod } from "./salesPeriod.ts";
import { downloadSalesCsv, toCsv } from "./salesCsv.ts";
import {
  csvColumnsOf,
  CustomerColumn,
  OrderColumn,
} from "./salesDetailsColumns.tsx";
import {
  activeColumnFilterCountOf,
  rowsMatchingColumnFilters,
  SalesColumnFilters,
  SalesColumnFilterValue,
} from "./salesColumnFilters.ts";
import { SalesDetailsTable } from "./SalesDetailsTable.tsx";
import { ChoiceControl } from "./ChoiceControl.tsx";
import { CsvButton } from "./CsvButton.tsx";
import { LoadingBlock, StatisticsCard } from "./StatisticsCard.tsx";

enum StatisticsCardView {
  Summary = "summary",
  Orders = "orders",
  Customers = "customers",
}

const VIEW_OPTIONS = [
  { value: StatisticsCardView.Summary, label: "Riepilogo" },
  { value: StatisticsCardView.Orders, label: "Per ordine" },
  { value: StatisticsCardView.Customers, label: "Per cliente" },
];

const NO_MATCHING_ROWS_MESSAGE =
  "Nessun risultato con la ricerca e i filtri scelti";

function orderKeyOf(order: SaleRecord): string {
  return `${order.gateway}:${order.orderId}`;
}

function withFilter(
  filters: SalesColumnFilters,
  header: string,
  value: SalesColumnFilterValue,
): SalesColumnFilters {
  return { ...filters, [header]: value };
}

export function StatisticsCardWithDetails({
  title,
  info,
  description,
  csvSection,
  period,
  isLoading,
  summary,
  exportSummaryCsv,
  orders,
  orderColumns,
  customerColumns,
  rankCustomersBy,
  noOrdersMessage,
}: {
  title: string;
  info: string;
  description: string;
  csvSection: string;
  period: SalesPeriod;
  isLoading: boolean;
  summary: ReactNode;
  exportSummaryCsv?: () => void;
  orders: SaleRecord[];
  orderColumns: OrderColumn[];
  customerColumns: CustomerColumn[];
  rankCustomersBy: (customer: CustomerOrders) => number;
  noOrdersMessage: string;
}) {
  const [view, setView] = useState(StatisticsCardView.Summary);
  const [customerSearch, setCustomerSearch] = useState("");
  const [orderFilters, setOrderFilters] = useState<SalesColumnFilters>({});
  const [customerFilters, setCustomerFilters] = useState<SalesColumnFilters>(
    {},
  );

  const allOrders = useMemo(() => newestOrdersFirst(orders), [orders]);
  const allCustomers = useMemo(
    () =>
      ordersByCustomer(allOrders).sort(
        (first, second) => rankCustomersBy(second) - rankCustomersBy(first),
      ),
    [allOrders, rankCustomersBy],
  );
  const shownOrders = rowsMatchingColumnFilters(
    allOrders.filter((order) =>
      matchesCustomerSearch(order.customer, customerSearch),
    ),
    orderColumns,
    orderFilters,
  );
  const shownCustomers = rowsMatchingColumnFilters(
    allCustomers.filter(({ customer }) =>
      matchesCustomerSearch(customer, customerSearch),
    ),
    customerColumns,
    customerFilters,
  );

  const csvExports: Record<StatisticsCardView, (() => void) | undefined> = {
    [StatisticsCardView.Summary]: exportSummaryCsv,
    [StatisticsCardView.Orders]:
      shownOrders.length > 0
        ? () =>
            downloadSalesCsv({
              section: `${csvSection}-per-ordine`,
              period,
              csv: toCsv(shownOrders, csvColumnsOf(orderColumns)),
            })
        : undefined,
    [StatisticsCardView.Customers]:
      shownCustomers.length > 0
        ? () =>
            downloadSalesCsv({
              section: `${csvSection}-per-cliente`,
              period,
              csv: toCsv(shownCustomers, csvColumnsOf(customerColumns)),
            })
        : undefined,
  };
  const exportCsv = csvExports[view];
  const isShowingOrders = view === StatisticsCardView.Orders;
  const isShowingDetails = view !== StatisticsCardView.Summary;
  const activeFilterCount = activeColumnFilterCountOf(
    isShowingOrders ? orderFilters : customerFilters,
  );
  const clearShownFilters = () =>
    isShowingOrders ? setOrderFilters({}) : setCustomerFilters({});

  return (
    <StatisticsCard
      title={title}
      info={info}
      description={description}
      spansWholeRow={isShowingDetails}
      actions={
        <>
          <ChoiceControl
            label="Vista"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
          {exportCsv && <CsvButton onClick={exportCsv} />}
        </>
      }
    >
      {!isShowingDetails ? (
        summary
      ) : isLoading ? (
        <LoadingBlock className="h-48 w-full" />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-72">
              <DebouncedSearchInput
                placeholder="Cerca per nome o email"
                value={customerSearch}
                onSearch={setCustomerSearch}
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearShownFilters}
                className="text-sm text-blue-600 hover:underline"
              >
                Rimuovi{" "}
                {activeFilterCount === 1
                  ? "il filtro"
                  : `i ${activeFilterCount} filtri`}
              </button>
            )}
          </div>
          {isShowingOrders ? (
            <SalesDetailsTable
              key={view}
              rows={shownOrders}
              unfilteredRows={allOrders}
              columns={orderColumns}
              filters={orderFilters}
              onFilter={(header, value) =>
                setOrderFilters((filters) => withFilter(filters, header, value))
              }
              rowKeyOf={orderKeyOf}
              noRowsMessage={noOrdersMessage}
              noMatchingRowsMessage={NO_MATCHING_ROWS_MESSAGE}
            />
          ) : (
            <SalesDetailsTable
              key={view}
              rows={shownCustomers}
              unfilteredRows={allCustomers}
              columns={customerColumns}
              filters={customerFilters}
              onFilter={(header, value) =>
                setCustomerFilters((filters) =>
                  withFilter(filters, header, value),
                )
              }
              rowKeyOf={({ customer }) => customerKeyOf(customer)}
              noRowsMessage={noOrdersMessage}
              noMatchingRowsMessage={NO_MATCHING_ROWS_MESSAGE}
            />
          )}
        </div>
      )}
    </StatisticsCard>
  );
}
